import React from 'react';

/**
 * Renders clause content with basic markdown support:
 * - **bold** → <strong>
 * - Lines starting with a), b), c) etc. → indented with flex layout
 * - Lines starting with "- " → indented bullet list with flex layout
 *
 * Uses flexbox per indented line so the marker (•, a), b) …) and the
 * text body are separate flex children.  This gives a pixel-perfect
 * "hanging indent" regardless of marker width.
 */
export function renderClauseContent(text: string): React.ReactNode {
  const lines = text.split('\n');

  return (
    <div className="whitespace-pre-line leading-relaxed">
      {lines.map((line, i) => {
        const subItemMatch = line.match(/^(\s*[a-z]\)\s)(.*)/);
        const bulletMatch = line.match(/^\s*-\s(.*)/);

        if (subItemMatch) {
          // e.g. "a) Some text…"
          const marker = subItemMatch[1].trim(); // "a)"
          const content = subItemMatch[2];
          return (
            <div key={i} className="flex gap-2 pl-4" data-bullet>
              <span className="shrink-0">{parseBold(marker)}</span>
              <span>{parseBold(content)}</span>
            </div>
          );
        }

        if (bulletMatch) {
          // e.g. "- Some text…"
          const content = bulletMatch[1];
          return (
            <div key={i} className="flex gap-2 pl-4" data-bullet>
              <span className="shrink-0">•</span>
              <span>{parseBold(content)}</span>
            </div>
          );
        }

        // Normal line
        const parts = parseBold(line);
        return (
          <span key={i}>
            {parts}
            {i < lines.length - 1 ? '\n' : null}
          </span>
        );
      })}
    </div>
  );
}

function parseBold(text: string): React.ReactNode {
  const regex = /\*\*(.+?)\*\*/g;
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    parts.push(<strong key={match.index}>{match[1]}</strong>);
    lastIndex = regex.lastIndex;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts.length === 1 ? parts[0] : parts;
}
